import api from './api';
import { translateError } from '../utils/errorTranslations';

/**
 * Obtém todas as transações de um usuário
 * @param {string} username - Nome do usuário
 * @returns {Promise<object>} Lista de transações
 */
export const getUserTransactions = async (username) => {
  try {
    const { data } = await api.get(`/transactions/${username}`);
    
    // Verificar se a resposta tem o formato esperado e extrair as transações
    if (data && data.transactions && Array.isArray(data.transactions)) {
      return data.transactions;
    } else if (data && Array.isArray(data)) {
      return data;
    } else {
      console.error('Resposta da API em formato inesperado:', data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching user transactions:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao obter transações do usuário'));
  }
};

/**
 * Cria uma nova transação
 * @param {object} transaction - Dados da transação
 * @param {string} transaction.username - Nome do usuário
 * @param {string} transaction.type - Tipo da transação (buy, sell, reinvestment)
 * @param {number} transaction.quantity - Quantidade de Bitcoin
 * @param {number} transaction.price - Preço do Bitcoin
 * @param {number} transaction.value - Valor total da transação (opcional)
 * @param {number} transaction.rsi - RSI no momento da transação (opcional)
 * @param {string} transaction.regime - Regime no momento da transação (opcional)
 * @param {boolean} processDynamicBalance - Se deve processar ajustes no dynamic balance (opcional, padrão: false)
 * @returns {Promise<object>} Transação criada com dados atualizados do usuário
 */
export const createTransaction = async (transaction, processDynamicBalance = false) => {
  try {
    console.log('🚀 [createTransaction] Iniciando criação de transação');
    console.log('🔍 [createTransaction] Dados recebidos:', {
      type: transaction.type,
      username: transaction.username,
      value: transaction.value,
      quantity: transaction.quantity,
      price: transaction.price,
      rsi: transaction.rsi,
      regime: transaction.regime,
      processDynamicBalance,
      timestamp: new Date().toISOString()
    });

    // ✅ NOVO: Incluir flag de processamento do dynamic balance e formatação de valores
    const requestData = {
      ...transaction,
      // Arredondar price para 2 casas decimais (padrão comercial)
      price: parseFloat(parseFloat(transaction.price).toFixed(2)),
      // Arredondar value para 2 casas decimais também
      value: parseFloat(parseFloat(transaction.value).toFixed(2)),
      // Arredondar quantity para 8 casas decimais (padrão Bitcoin)
      quantity: parseFloat(parseFloat(transaction.quantity).toFixed(8)),
      // Arredondar RSI para 2 casas decimais
      rsi: transaction.rsi ? parseFloat(parseFloat(transaction.rsi).toFixed(2)) : transaction.rsi,
      process_dynamic_balance: processDynamicBalance
    };

    console.log('🔍 [createTransaction] Dados que serão enviados para API (formatados):', requestData);
    console.log('🔍 [createTransaction] URL da API:', '/transactions');
    console.info('💰 [DYNAMIC CASH] process_dynamic_balance:', requestData.process_dynamic_balance);
    console.info('💰 [DYNAMIC CASH] use_dynamic_balance:', requestData.use_dynamic_balance);
    
    if (requestData.use_dynamic_balance) {
      console.warn('⚠️ [DYNAMIC CASH] Esta transação vai DEDUZIR DO CAIXA!');
    }

    const { data } = await api.post('/transactions', requestData);
    
    // ✅ FASE 2: Log dos dados retornados pela API
    console.log('✅ Transaction created successfully:', {
      success: data.success,
      transaction_id: data.transaction_id,
      has_updated_user_settings: !!data.updated_user_settings,
      has_updated_portfolio: !!data.updated_portfolio,
      dynamic_balance_change: data.transaction_details?.dynamic_balance_change || 0,
      dynamic_balance_processed: data.transaction_details?.dynamic_balance_processed || false
    });

    // ✅ FASE 2: Log detalhado do dynamic balance se disponível
    if (data.updated_user_settings) {
      console.info('✅ [DYNAMIC CASH] ATUALIZADO NO BACKEND!', {
        new_balance: data.updated_user_settings.dynamic_contribution_balance,
        base_contribution: data.updated_user_settings.base_contribution,
        change: data.transaction_details?.dynamic_balance_change || 0,
        processed: data.transaction_details?.dynamic_balance_processed || false
      });
    } else {
      console.warn('⚠️ [DYNAMIC CASH] BACKEND NÃO RETORNOU updated_user_settings!');
      console.debug('📊 [DYNAMIC CASH] Response completa:', data);
    }
    
    // ✅ NOVO: Log explícito se Dynamic Cash foi processado
    if (data.transaction_details?.dynamic_balance_processed) {
      console.info('✅ [DYNAMIC CASH] Processamento confirmado pelo backend');
    } else {
      console.warn('⚠️ [DYNAMIC CASH] Backend indica que NÃO processou Dynamic Cash');
    }

    // ✅ FASE 2: Log do portfolio atualizado se disponível
    if (data.updated_portfolio) {
      console.log('📊 Portfolio updated:', {
        btc_balance: data.updated_portfolio.btc_balance,
        total_invested: data.updated_portfolio.total_invested,
        current_value: data.updated_portfolio.current_value,
        profit_loss: data.updated_portfolio.profit_loss
      });
    }

    // ✅ NOVO: Disparar evento de transação criada para atualizar outras páginas
    window.dispatchEvent(new CustomEvent('transaction-created', {
      detail: {
        transactionId: data.transaction_id,
        type: transaction.type,
        username: transaction.username,
        timestamp: new Date().toISOString()
      }
    }));
    console.log('📡 [createTransaction] Event "transaction-created" dispatched');

    return data;
  } catch (error) {
    console.error('❌ [createTransaction] Erro ao criar transação:', error);
    console.error('❌ [createTransaction] Tipo do erro:', error.constructor.name);
    console.error('❌ [createTransaction] Status:', error.response?.status);
    console.error('❌ [createTransaction] Status Text:', error.response?.statusText);
    console.error('❌ [createTransaction] Response Data:', error.response?.data);
    console.error('❌ [createTransaction] Request Config:', {
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      data: error.config?.data
    });
    console.error('❌ [createTransaction] Stack trace:', error.stack);
    
    // Criar mensagem de erro mais detalhada
    let errorMessage = translateError('Erro ao criar transação');
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    // Adicionar informações do status HTTP se disponível
    if (error.response?.status) {
      errorMessage = `HTTP ${error.response.status}: ${errorMessage}`;
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Registra uma transação manual
 * @param {object} transaction - Dados da transação
 * @param {string} transaction.username - Nome do usuário
 * @param {string} transaction.transaction_type - Tipo da transação
 * @param {number} transaction.quantity - Quantidade de Bitcoin
 * @param {number} transaction.price - Preço do Bitcoin
 * @param {number} transaction.value - Valor total da transação
 * @param {number} transaction.rsi - RSI no momento da transação
 * @param {string} transaction.regime - Regime no momento da transação
 * @returns {Promise<object>} Transação registrada
 */
export const registerManualTransaction = async (transaction) => {
  try {
    // Formatação dos valores para padrões comerciais
    const formattedTransaction = {
      ...transaction,
      // Arredondar price para 2 casas decimais (padrão comercial)
      price: parseFloat(parseFloat(transaction.price).toFixed(2)),
      // Arredondar value para 2 casas decimais também
      value: parseFloat(parseFloat(transaction.value).toFixed(2)),
      // Arredondar quantity para 8 casas decimais (padrão Bitcoin)
      quantity: parseFloat(parseFloat(transaction.quantity).toFixed(8)),
      // Arredondar RSI para 2 casas decimais
      rsi: transaction.rsi ? parseFloat(parseFloat(transaction.rsi).toFixed(2)) : transaction.rsi
    };

    console.log('🔍 [registerManualTransaction] Dados formatados:', formattedTransaction);
    
    const { data } = await api.post('/transactions/manual/', formattedTransaction);

    window.dispatchEvent(new CustomEvent('transaction-created', {
      detail: {
        transactionId: data.transaction_id,
        type: formattedTransaction.transaction_type,
        username: formattedTransaction.username,
        timestamp: new Date().toISOString(),
        is_manual: true,
      }
    }));

    return data;
  } catch (error) {
    console.error('Error registering manual transaction:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao registrar transação manual'));
  }
};

/**
 * Edita uma transação manual por ID.
 * @param {number} transactionId
 * @param {object} data - { username, quantity, price, value, date?, rsi?, regime? }
 */
export const updateManualTransaction = async (transactionId, data) => {
  try {
    const payload = {
      ...data,
      price: parseFloat(parseFloat(data.price).toFixed(2)),
      value: parseFloat(parseFloat(data.value).toFixed(2)),
      quantity: parseFloat(parseFloat(data.quantity).toFixed(8)),
      rsi: data.rsi ? parseFloat(parseFloat(data.rsi).toFixed(2)) : data.rsi,
    };
    const { data: response } = await api.patch(`/transactions/manual/${transactionId}/`, payload);
    window.dispatchEvent(new CustomEvent('transaction-updated', {
      detail: { transactionId, timestamp: new Date().toISOString() }
    }));
    return response;
  } catch (error) {
    console.error('Error updating manual transaction:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao editar transação manual'));
  }
};

/**
 * Remove uma transação por ID (método mais confiável)
 * 🚨 CRÍTICO: NUNCA ALTERAR ESTA FUNÇÃO - É A SOLUÇÃO PARA O PROBLEMA DE DELETE
 * ✅ Usa POST em vez de DELETE
 * ✅ Usa transaction_id em vez de username/date/type
 * ✅ Elimina problemas de codificação de URL e microssegundos
 * @param {number} transactionId - ID da transação
 * @returns {Promise<object>} Resultado da remoção
 */
export const deleteTransaction = async (transactionId) => {
  try {
    console.log('🗑️ [deleteTransaction] Deleting transaction with ID:', transactionId);
    
    // 🔒 NUNCA ALTERAR: Método POST para /transactions/delete/
    const { data } = await api.post('/transactions/delete/', {
      transaction_id: transactionId  // 🔒 NUNCA ALTERAR: Parâmetro transaction_id
    });
    
    console.log('✅ [deleteTransaction] Transaction deleted successfully');
    
    // ✅ NOVO: Disparar evento de transação deletada para atualizar outras páginas
    window.dispatchEvent(new CustomEvent('transaction-deleted', {
      detail: {
        transactionId,
        timestamp: new Date().toISOString()
      }
    }));
    console.log('📡 [deleteTransaction] Event "transaction-deleted" dispatched');
    
    return data;
  } catch (error) {
    console.error('❌ [deleteTransaction] Error deleting transaction:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao remover transação'));
  }
};

/**
 * Remove uma transação (método legado - mantido para compatibilidade)
 * @param {string} username - Nome do usuário
 * @param {string} date - Data da transação (timestamp completo)
 * @param {string} type - Tipo da transação
 * @returns {Promise<object>} Resultado da remoção
 * @deprecated Use deleteTransaction(transactionId) em vez disso
 */
export const deleteTransactionLegacy = async (username, date, type) => {
  try {
    // Manter o timestamp completo como recebido do banco de dados
    // Não fazer nenhuma conversão de formato para preservar a precisão
    let formattedDate = date;
    
    // Se for um objeto Date, converter para string no formato esperado
    if (date instanceof Date) {
      formattedDate = date.toISOString().replace('T', ' ').split('.')[0];
    } else if (typeof date === 'string') {
      // Se a string contém 'T' (formato ISO), converter para formato com espaço
      if (date.includes('T')) {
        formattedDate = date.replace('T', ' ').split('.')[0].split('+')[0].split('Z')[0];
      }
      // Se já está no formato correto (YYYY-MM-DD HH:MM:SS), manter como está
    }
    
    console.log('Deleting transaction with date:', formattedDate);
    
    const { data } = await api.delete(`/transactions/${username}/${encodeURIComponent(formattedDate)}/${type}`);
    return data;
  } catch (error) {
    console.error('Error deleting transaction:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao remover transação'));
  }
};

/**
 * Executa operações (contribuição, venda, recompra)
 * @param {object} operations - Operações a serem executadas
 * @returns {Promise<object>} Resultado das operações
 */
export const executeOperations = async (operations) => {
  try {
    const { data } = await api.post('/operations/', operations);
    return data;
  } catch (error) {
    console.error('Error executing operations:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao executar operações'));
  }
};

/**
 * Valida operações antes de executá-las
 * @param {object} operations - Operações a serem validadas
 * @returns {Promise<object>} Resultado da validação
 */
export const validateOperations = async (operations) => {
  try {
    const { data } = await api.post('/operations/validate/', operations);
    return data;
  } catch (error) {
    console.error('Error validating operations:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao validar operações'));
  }
};

/**
 * Verifica se o usuário já fez trades na semana atual
 * @param {string} username - Nome do usuário
 * @returns {Promise<object>} Informações sobre trades semanais
 */
export const checkWeeklyTrades = async (username) => {
  try {
    // Obter todas as transações do usuário
    const transactions = await getUserTransactions(username);
    
    // Calcular início e fim da semana atual (domingo a sábado)
    const now = new Date();
    const currentDay = now.getDay(); // 0 = domingo, 1 = segunda, etc.
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - currentDay);
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    // Todas as transações da semana (incluindo manuais) — para exibição de investido/média
    const allWeeklyTrades = transactions.filter(tx => {
      const txDate = new Date(tx.date);
      return txDate >= startOfWeek && txDate <= endOfWeek;
    });

    // Apenas transações de sistema (excluindo manuais) — para lógica de bloqueio/tradeCounts
    const weeklyTrades = allWeeklyTrades.filter(tx => !tx.is_manual);

    // Contar tipos de transações (somente sistema, manuais não bloqueiam)
    const tradeCounts = {
      buy: 0,
      sell: 0,
      reinvestment: 0
    };

    weeklyTrades.forEach(tx => {
      if (tradeCounts.hasOwnProperty(tx.type)) {
        tradeCounts[tx.type]++;
      }
    });

    // Verificar se há violações das regras semanais
    const violations = [];
    if (tradeCounts.buy > 1) violations.push(`Multiple dynamic investments (${tradeCounts.buy})`);
    if (tradeCounts.sell > 1) violations.push(`Multiple sell transactions (${tradeCounts.sell})`);
    if (tradeCounts.reinvestment > 1) violations.push(`Multiple reinvestment transactions (${tradeCounts.reinvestment})`);

    // Verificar se há venda junto com operação de compra
    if (tradeCounts.sell > 0 && (tradeCounts.buy > 0 || tradeCounts.reinvestment > 0)) {
      violations.push('Sell transaction cannot happen with buy or reinvestment in the same week');
    }

    return {
      weeklyTrades,
      allWeeklyTrades,
      tradeCounts,
      violations,
      hasWeeklyTrades: weeklyTrades.length > 0,
      canTrade: violations.length === 0,
      weekStart: startOfWeek.toISOString(),
      weekEnd: endOfWeek.toISOString()
    };
  } catch (error) {
    console.error('Error checking weekly trades:', error);
    throw new Error('Erro ao verificar trades semanais');
  }
};

/**
 * ✅ NOVO: Verifica disponibilidade de fundos antes de executar uma operação
 * Implementa a Trava de Liquidez - verifica se há saldo suficiente (Aporte Base + Caixa)
 * 
 * @param {string} username - Nome do usuário
 * @param {number} requiredAmount - Valor necessário (recomendação do sistema)
 * @param {number} baseContribution - Aporte base configurado
 * @returns {Promise<object>} Resultado da verificação
 */
export const checkFundsAvailability = async (username, requiredAmount, baseContribution) => {
  try {
    console.log('💰 [checkFundsAvailability] Verificando disponibilidade de fundos:', {
      username,
      requiredAmount,
      baseContribution
    });
    
    const { data } = await api.post(`/dynamic-cash/${username}/check-funds/`, {
      required_amount: requiredAmount,
      base_contribution: baseContribution
    });
    
    console.log('✅ [checkFundsAvailability] Resultado da verificação:', data);
    
    return data;
  } catch (error) {
    console.error('❌ [checkFundsAvailability] Erro ao verificar fundos:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao verificar disponibilidade de fundos'));
  }
};
 